import React from "react";

import classnames from "classnames";
import {
  calculateIndividualCompanyCost,
  calculateIndividualEmployeeCost,
  calculateIndividualTotalCost,
  calculateIndividualDependentCompanyCost,
  calculateIndividualDependentEmployeeCost,
  calculateIndividualDependentTotalCost,
} from "EmployerApplication/Components/ComprehensiveCostEstimation/CostCalculations";
import { formatEmployeeInfo } from "EmployerApplication/Components/ComprehensiveCostEstimation/EmployeeDetailsBody";
import { getDependentsDecliningAllPlans, employeeCopyWithFullCoverage } from "EmployerApplication/Components/ComprehensiveCostEstimation/Utils";
import { connect } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import simplifyPlanName from "Utils/simplifyPlanName";

import { TR, TD } from "Cobalt/TableV2";
import BodyText from "Common/BodyText";
import { TIERED_CONTRIBUTION_ROLES, ALL_ROLES, CONTRIBUTION_TYPES } from "Common/Constants";
import Icon from "Common/Icon";

import DependentsTable from "./DependentsTable";

import makeBem from "Utils/makeBem";

const bem = makeBem("CostBreakdown");

const EmployeeRowDumb = ({
  employee,
  role,
  is_enrolling,
  index,
  medicalPlans,
  contributionType,
  contributions,
  noEmployeeEnrollments,
  dentalPlan,
  visionPlan,
}) => {
  const contributionOptions = {};
  contributions.forEach((contribution) => {
    contributionOptions[TIERED_CONTRIBUTION_ROLES[contribution.role]] = contribution.role;
  });

  const planOptions = {};
  medicalPlans.forEach((plan) => {
    planOptions[simplifyPlanName(plan)] = plan.id;
  });

  const selectedPlan = medicalPlans.find((plan) => plan.id === employee.selected_medical_plan) || medicalPlans[0];

  const selectedContribution = contributions.find((contribution) => contribution.role == role) || contributions[0];

  const employeeTier = ALL_ROLES[employee.role];

  const dependentsDecliningAllPlans = getDependentsDecliningAllPlans(employee, dentalPlan, visionPlan);

  const employeeCopy = employeeCopyWithFullCoverage(employee);

  const employeeCost = formatCurrency(
    calculateIndividualEmployeeCost(index, selectedPlan, contributionType, selectedContribution, medicalPlans, dentalPlan, visionPlan, employee),
  );

  const companyCost = formatCurrency(
    calculateIndividualCompanyCost(contributionType, selectedContribution, index, selectedPlan, medicalPlans, dentalPlan, visionPlan, employee),
  );

  const totalCost = formatCurrency(calculateIndividualTotalCost(index, selectedPlan, dentalPlan, visionPlan, employee));

  const hasTieredContributions = contributions.length > 1;

  const enrollmentDecisionCompleted = is_enrolling !== undefined;
  const dentalPlanOffered = dentalPlan !== undefined && dentalPlan !== null;
  const visionPlanOffered = visionPlan !== undefined && visionPlan !== null;
  const enrollmentNoticeColumnSpan = [dentalPlanOffered, visionPlanOffered].filter((x) => x).length + 2;

  const stripedRow = index % 2 === 0;

  const dependentsRowColumnSpan = [
    "dependentCostsColumn",
    "ageColumn",
    hasTieredContributions ? "contributionColumn" : undefined,
    "enrollingMedicalColumn",
    "selectedPlanColumn",
    dentalPlan ? "dentalPlanColumn" : undefined,
    visionPlan ? "visionPlanColumn" : undefined,
  ].filter((col) => col !== undefined && col !== null).length;

  const noEnrollmentsNoticeColumnSpan = [
    undefined, // tiered contribution column
    "enrollingMedicalColumn",
    "selectedPlanColumn",
    dentalPlanOffered ? "dentalPlanColumn" : undefined,
    visionPlanOffered ? "visionPlanColumn" : undefined,
    "individualCostColumn",
    "companyCostColumn",
  ].filter((col) => col !== undefined && col !== null).length;

  return (
    <>
      <TR
        index={`employee${index}`}
        className={classnames(bem("EmployeeDetailsRow"), {
          "BEMTable__Row--striped": stripedRow,
        })}
      >
        <TD className={bem("EmployeeDetailsColumn")}>{employee.name}</TD>
        <TD className={bem("EmployeeDetailsColumnThin")}>{employee.age}</TD>
        {hasTieredContributions && <TD>{employeeTier}</TD>}
        {/* NO ENROLLMENT DECISIONS COMPLETED */}
        {noEmployeeEnrollments && index === 0 && (
          <>
            {index == 0 && (
              <>
                <TD colSpan={noEnrollmentsNoticeColumnSpan} rowSpan={999} className={classnames(bem("EnrollmentNotice"), "text-center")}>
                  <NoEnrollmentsNotice />
                </TD>
                <TD className={bem("TotalCostColumn")} rowSpan={999} />
              </>
            )}
          </>
        )}

        {/* COMPLETED AT LEAST ONE ENROLLMENT DECISION */}
        {!noEmployeeEnrollments && (
          <>
            {!enrollmentDecisionCompleted && (
              <TD className={classnames(bem("EmployeeDetailsColumnThin"))} colSpan={enrollmentNoticeColumnSpan}>
                <BodyText>Awaiting enrollment decision</BodyText>
              </TD>
            )}

            {enrollmentDecisionCompleted && (
              <TD className={classnames(bem("EmployeeDetailsColumn"))}>
                {is_enrolling && selectedPlan && <BodyText>{simplifyPlanName(selectedPlan)}</BodyText>}
              </TD>
            )}

            {enrollmentDecisionCompleted && (
              <>
                {dentalPlanOffered && (
                  <TD className={bem("EmployeeDetailsColumnThin")}>
                    {employee.is_enrolling_dental !== undefined ? (
                      employee.is_enrolling_dental ? (
                        <>
                          <Icon icon="check" />
                          <span className="sr-only">Enrolling in dental</span>
                        </>
                      ) : (
                        <>
                          <Icon icon="ban" />
                          <span className="sr-only">Not enrolling in dental</span>
                        </>
                      )
                    ) : null}
                  </TD>
                )}

                {visionPlanOffered && (
                  <TD className={bem("EmployeeDetailsColumnThin")}>
                    {employee.is_enrolling_vision !== undefined ? (
                      employee.is_enrolling_vision ? (
                        <>
                          <Icon icon="check" />
                          <span className="sr-only">Enrolling in vision</span>
                        </>
                      ) : (
                        <>
                          <Icon icon="ban" />
                          <span className="sr-only">Not enrolling in vision</span>
                        </>
                      )
                    ) : null}
                  </TD>
                )}
              </>
            )}

            {enrollmentDecisionCompleted ? (
              <>
                <TD align="right">{employeeCost === "$NaN" ? "$0" : employeeCost}</TD>
                <TD align="right">{companyCost === "$NaN" ? "$0" : companyCost}</TD>
                <TD align="right">{totalCost === "$NaN" ? "$0" : totalCost}</TD>
              </>
            ) : (
              <>
                <TD align="right">{/* individual cost spacer */}</TD>
                <TD align="right">{/* company cost spacer */}</TD>
                <TD align="right">{/* total cost spacer */}</TD>
              </>
            )}
          </>
        )}
      </TR>
      {employee?.dependents?.length > 0 &&
        (contributionType == CONTRIBUTION_TYPES.fixed ||
          selectedPlan?.pricing_type === "segment" ||
          contributionType === CONTRIBUTION_TYPES.percent_of_base_plan) &&
        enrollmentDecisionCompleted && (
          <TR
            index={`employee${index}.dependentsCostRow`}
            className={classnames(bem("EmployeeDetailsRow"), {
              "BEMTable__Row--striped": stripedRow,
            })}
          >
            <TD colSpan={dependentsRowColumnSpan} className={bem("EmployeeDetailsColumn")}>
              &nbsp;Dependent Costs
            </TD>
            {!noEmployeeEnrollments && (
              <>
                <TD align="right">
                  {formatCurrency(
                    is_enrolling
                      ? calculateIndividualDependentEmployeeCost(
                          selectedContribution,
                          index,
                          selectedPlan,
                          dentalPlan,
                          visionPlan,
                          dependentsDecliningAllPlans ? employeeCopy : employee,
                          employee,
                        )
                      : 0,
                  )}
                </TD>
                <TD align="right">
                  {formatCurrency(
                    is_enrolling
                      ? calculateIndividualDependentCompanyCost(
                          selectedContribution,
                          dependentsDecliningAllPlans ? employeeCopy : employee,
                          index,
                          selectedPlan,
                        )
                      : 0,
                  )}
                </TD>
                <TD align="right">
                  {formatCurrency(is_enrolling ? calculateIndividualDependentTotalCost(index, employee, selectedPlan, dentalPlan, visionPlan) : 0)}
                </TD>
              </>
            )}
          </TR>
        )}

      <DependentsTable
        component={DependentsTable}
        employee={employee}
        selectedPlan={selectedPlan}
        contributionType={contributionType}
        contributions={contributions}
        contribution={selectedContribution}
        hasMultiplePlans={medicalPlans.length > 1}
        hasTieredContributions={contributions.length > 1}
        employeeIndex={index}
        medicalPlans={medicalPlans}
        noEmployeeEnrollments={noEmployeeEnrollments}
        dentalPlan={dentalPlan}
        visionPlan={visionPlan}
        stripedRow={stripedRow}
      />
    </>
  );
};

const NoEnrollmentsNotice = () => <BodyText size="x-large">Awaiting enrollment decisions.</BodyText>;

const mapStateToProps = (_state, { employee }) => ({
  name: employee.name,
  employee,
  role: employee.role,
  planId: employee.planId,
  is_enrolling: employee.is_enrolling,
});

const withReduxEmployeeRow = connect(mapStateToProps);

const EmployeeRow = withReduxEmployeeRow(EmployeeRowDumb);

const EmployeesTable = (props) => {
  const noEmployeeEnrollments = props.employees.every((employee) => employee.is_enrolling === undefined);

  return (
    <>
      {props.employees.map((employee, index) => (
        <EmployeeRow
          key={index}
          employee={formatEmployeeInfo(employee)}
          index={index}
          fields={props.fields}
          medicalPlans={props.medicalPlans}
          contributionType={props.contributionType}
          contributions={props.contributions}
          companyIDToken={props.companyIDToken}
          inputName={employee}
          noEmployeeEnrollments={noEmployeeEnrollments}
          dentalPlan={props.dentalPlan}
          visionPlan={props.visionPlan}
        />
      ))}
    </>
  );
};

export default EmployeesTable;
